import type Tests from '@lved/ui/components/shared/ui/Tabs/Tests';
import { useTestsStore } from '@lved/ui/store/testsStore';
import { ReactWidget } from '@theia/core/lib/browser';
import { injectable, postConstruct } from '@theia/core/shared/inversify';

type TestsModule = { Tests: typeof Tests };

@injectable()
export class TestsPanel extends ReactWidget {
    static readonly ID: string = 'widget:lvgl-tests';
    static readonly LABEL: string = 'Tests';

    protected module: TestsModule | undefined;
    protected loadError: string | undefined;

    @postConstruct()
    protected init(): void {
        this.doInit();
    }

    protected async doInit(): Promise<void> {
        this.id = TestsPanel.ID;
        this.title.label = TestsPanel.LABEL;
        this.title.caption = TestsPanel.LABEL;
        this.title.closable = true;
        await this.loadModule();
        this.update();
    }

    protected async loadModule(): Promise<void> {
        try {
            this.module = (await import('@lved/ui')) as unknown as TestsModule;
        } catch (error: unknown) {
            this.loadError = error instanceof Error ? error.message : 'Failed to load Tests panel.';
        }
    }

    runTest(): void {
        useTestsStore.getState().runTest();
    }

    setSpeed(value: number): void {
        useTestsStore.getState().setSpeed(value);
    }

    canRunTest(): boolean {
        const { mode, canRun, isRunning } = useTestsStore.getState();
        return (mode === 'single' || mode === 'project') && (isRunning || canRun);
    }

    canSetSpeed(): boolean {
        const { mode, isRunning } = useTestsStore.getState();
        return mode === 'single' && !isRunning;
    }

    protected render(): JSX.Element {
        if (this.loadError) return <p>{this.loadError}</p>;
        if (!this.module) return <p>Loading Tests...</p>;
        const { Tests } = this.module;
        return <Tests />;
    }
}
